import { useCallback } from "react";
import { Button, Divider, Group, Modal } from "@mantine/core";
import SourceSubsiteForm, {
  SourceSubsiteFormValues,
} from "../admin/sourcesubsites/SourceSubsiteForm";
import { useCreateSourceSubsite } from "../../hooks/admin/useSourceSubsites";

const FORM_ID = "accessioning-add-subsite-form";

interface SourceContext {
  id: string;
  name: string;
}

interface AddSubsiteModalProps {
  opened: boolean;
  onClose: () => void;
  sources: SourceContext[];
  preselectedSourceId?: string;
  existingSubsiteNamesBySource: Record<string, string[]>;
  initialName: string;
  onCreated: (subsiteId: string, sourceId: string) => void;
}

export default function AddSubsiteModal({
  opened,
  onClose,
  sources,
  preselectedSourceId,
  existingSubsiteNamesBySource,
  initialName,
  onCreated,
}: AddSubsiteModalProps) {
  const createSubsite = useCreateSourceSubsite();

  const handleSubmit = useCallback(
    async (values: SourceSubsiteFormValues) => {
      const description = values.description.trim();
      const created = await createSubsite.mutateAsync({
        name: values.name.trim(),
        sourceId: values.sourceId,
        ...(description ? { description } : {}),
      });
      onCreated(created.id, values.sourceId);
      onClose();
    },
    [createSubsite, onClose, onCreated]
  );

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      size="md"
      centered
      closeOnClickOutside={false}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Add New Subsite</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          {opened && (
            <SourceSubsiteForm
              onSubmit={handleSubmit}
              sources={sources}
              preselectedSourceId={preselectedSourceId}
              initialName={initialName}
              existingSubsiteNamesBySource={existingSubsiteNamesBySource}
              formId={FORM_ID}
              autoFocusName
            />
          )}
        </Modal.Body>
        <Divider />
        <Group justify="space-between" p="md">
          <Button variant="default" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={createSubsite.isPending}
          >
            Create subsite
          </Button>
        </Group>
      </Modal.Content>
    </Modal.Root>
  );
}
